import colombianFlag from "@/assets/colombianFlag.svg";
import DatePicker from "@/components/DatePicker";
import { formatUtcDatetimeToColombian } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { CreateInvoice, Invoice, PaymentDeadline } from "@/types/types";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Calendar, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { mapCancelationReason } from "../mappers/invoice.mapper";
import { usePermissions } from "@/hooks/usePermissions";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (invoiceData: CreateInvoice) => void;
  editInvoiceData?: Invoice;
};

const AddInvoiceModal = ({
  open,
  onClose,
  onSubmit,
  editInvoiceData,
}: Props): JSX.Element => {
  const formatCurrency = useNumberFormatter("es-CO", "COP", "number");
  const { merchant } = useMerchant();
  const { isEditor } = usePermissions();

  const [name, setName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [description, setDescription] = useState("");
  const [contract, setContract] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [deadlines, setDeadlines] = useState<
    { date: string; amount: number | null }[]
  >([{ date: "", amount: 0 }]);

  const invoiceData: CreateInvoice = useMemo(
    () => ({
      rentalsMerchantId: merchant?.id || "",
      customerName: name.trim() || undefined,
      customerDocumentNumber: documentNumber,
      customerEmail: email.trim() || undefined,
      customerPhoneNumber: phone ? `57${phone}` : undefined,
      description: description,
      contract: contract,
      paymentDeadlines: deadlines as [PaymentDeadline, ...PaymentDeadline[]],
      id: editInvoiceData?.id,
    }),
    [
      merchant,
      name,
      documentNumber,
      email,
      phone,
      description,
      contract,
      deadlines,
      editInvoiceData,
    ]
  );

  const resetForm = () => {
    setName("");
    setDocumentNumber("");
    setDescription("");
    setContract("");
    setEmail("");
    setPhone("");
    setDeadlines([{ date: "", amount: 0 }]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddDeadline = () => {
    if (deadlines.length < 5) {
      setDeadlines([...deadlines, { date: "", amount: 0 }]);
    }
  };

  const handleRemoveDeadline = (index: number) => {
    if (deadlines.length > 1) {
      setDeadlines(deadlines.filter((_, i) => i !== index));
    }
  };

  const handleDateChange = (index: number, d: string | undefined) => {
    const newDeadlines = [...deadlines];
    newDeadlines[index].date = d ?? "";
    setDeadlines(newDeadlines);
  };

  const handleAmountChange = (index: number, amount: number | undefined) => {
    const newDeadlines = [...deadlines];
    newDeadlines[index].amount = amount || 0;
    setDeadlines(newDeadlines);
  };

  const isFormValid = useCallback(() => {
    return (
      invoiceData.customerDocumentNumber &&
      invoiceData.description &&
      (!email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) &&
      (!phone || /^3[0-9]{9}$/.test(phone)) &&
      invoiceData.paymentDeadlines.length >= 1 &&
      invoiceData.paymentDeadlines.every(
        (date: { date: string; amount: number | null }) =>
          date.date !== "" && date.amount && date.amount >= 3000
      )
    );
  }, [invoiceData, email, phone]);

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(invoiceData);
      resetForm();
      onClose();
    }
  };

  useEffect(() => {
    if (editInvoiceData) {
      const deadlines = editInvoiceData.paymentDeadlines ?? [
        { date: editInvoiceData.dueDate, amount: editInvoiceData.amount },
      ];

      setName(editInvoiceData.customerName ?? "");
      setContract(editInvoiceData.contract ?? "");
      setDocumentNumber(editInvoiceData.customerDocumentNumber);
      setDescription(editInvoiceData.description);
      setDeadlines(deadlines);
    } else {
      resetForm();
    }
  }, [editInvoiceData, open]);

  const isEditableInvoice =
    isEditor &&
    (!editInvoiceData ||
      (editInvoiceData?.creationMethod === "manual" &&
        editInvoiceData?.status !== "cancelled" &&
        !editInvoiceData?.paymentDate));

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => !e.open && handleClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>
                  {editInvoiceData && isEditableInvoice
                    ? "Editar Recaudo"
                    : editInvoiceData
                    ? "Información de Recaudo"
                    : "Agregar Recaudo"}
                </Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={handleClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                {editInvoiceData && (
                  <HStack justify="space-between">
                    <Text fontWeight={600}>ID:</Text>
                    <Text>{editInvoiceData.id}</Text>
                  </HStack>
                )}
                {editInvoiceData ? (
                  <HStack justify="space-between">
                    <Text fontWeight={600}>Número de documento:</Text>
                    <Text>{editInvoiceData.customerDocumentNumber}</Text>
                  </HStack>
                ) : (
                  <Box>
                    <Text fontWeight={600}>Número de documento*</Text>
                    <Input
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(e.target.value)}
                      placeholder="0000000000"
                      disabled={!!editInvoiceData}
                    />
                  </Box>
                )}
                {editInvoiceData && (
                  <HStack justify="space-between">
                    <Text fontWeight={600} mb={2}>
                      Fecha de creación:
                    </Text>
                    <Text>
                      {formatUtcDatetimeToColombian(editInvoiceData.createdAt)}
                    </Text>
                  </HStack>
                )}
                {editInvoiceData?.status === "cancelled" &&
                  editInvoiceData.cancelationReason && (
                    <HStack justify="space-between">
                      <Text fontWeight={600} mb={2}>
                        Archivado por:
                      </Text>
                      <Badge>
                        {
                          mapCancelationReason[
                            editInvoiceData.cancelationReason as keyof typeof mapCancelationReason
                          ]
                        }
                      </Badge>
                    </HStack>
                  )}
                {editInvoiceData && editInvoiceData.sendDate && (
                  <HStack justify="space-between">
                    <Text fontWeight={600} mb={2}>
                      Próximo Envío:
                    </Text>
                    <Text>{editInvoiceData.sendDate}</Text>
                  </HStack>
                )}
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Nombre y apellido
                  </Text>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Juan Pérez"
                    disabled={!isEditableInvoice}
                  />
                </Box>
                {!editInvoiceData && (
                  <>
                    <Box>
                      <Text fontWeight={600} mb={2}>
                        Correo
                      </Text>
                      <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="juan@correo.com"
                      />
                    </Box>
                    <Box>
                      <Text fontWeight={600} mb={2}>
                        WhatsApp
                      </Text>
                      <HStack>
                        <Input value="+57" readOnly width="4rem" />
                        <Input
                          value={phone}
                          onChange={(e) =>
                            setPhone(
                              e.target.value
                                .replace(/\D/g, "")
                                .replace(/^57/, "")
                                .slice(0, 10)
                            )
                          }
                          placeholder="000 000 00 00"
                        />
                      </HStack>
                    </Box>
                  </>
                )}
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Contrato
                  </Text>
                  <Input
                    value={contract}
                    onChange={(e) => setContract(e.target.value)}
                    placeholder="00000"
                    disabled={!isEditableInvoice}
                  />
                </Box>
                <Box>
                  <Text fontWeight={600} mb={2}>
                    Concepto*
                  </Text>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Canon Arrendamiento C123 #24-89"
                    disabled={!isEditableInvoice}
                  />
                </Box>
                {deadlines.map((deadline, index) => (
                  <Box
                    key={index}
                    p={"0.5rem"}
                    border={"1px solid #ebecef80"}
                    borderRadius={"0.5rem"}
                  >
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Text fontWeight={600}>
                        Fecha y Valor {index + 1}
                        {index === 0 ? " *" : ""}
                      </Text>
                      {index > 0 && (
                        <IconButton
                          size="xs"
                          aria-label="Remove date"
                          onClick={() => handleRemoveDeadline(index)}
                          color={"#EC5858"}
                          bg={"#FFDCDC"}
                          borderRadius={"0.5rem"}
                          disabled={!isEditableInvoice}
                          mb={"0.25rem"}
                        >
                          <Trash2 size=".5rem" />
                        </IconButton>
                      )}
                    </Box>
                    <HStack>
                      <Box>
                        <DatePicker
                          placeholder="Fecha"
                          value={deadline.date}
                          setValue={(d) => handleDateChange(index, d)}
                          isDisabled={!isEditableInvoice}
                        />
                      </Box>
                      <Box position="relative" flex="1">
                        <Box
                          position="absolute"
                          left={2}
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                        >
                          <HStack gap={1}>
                            <Image
                              src={colombianFlag}
                              alt="Colombian flag"
                              boxSize="1em"
                            />
                            <Text fontSize="sm" color="gray.500">
                              COP
                            </Text>
                          </HStack>
                        </Box>
                        <Input
                          type="text"
                          value={
                            deadline.amount
                              ? formatCurrency(deadline.amount)
                              : ""
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            handleAmountChange(
                              index,
                              rawValue ? parseFloat(rawValue) : undefined
                            );
                          }}
                          placeholder="0"
                          pl="4rem"
                          disabled={!isEditableInvoice}
                        />
                      </Box>
                    </HStack>
                  </Box>
                ))}
                {deadlines.length < 5 && isEditableInvoice && (
                  <Button
                    onClick={handleAddDeadline}
                    variant="ghost"
                    color={"merchants.primary.app"}
                    alignSelf={"flex-start"}
                    p={"0.25rem 0.5rem"}
                  >
                    <Calendar /> Agregar nueva fecha
                  </Button>
                )}
                {isEditableInvoice && (
                  <HStack gap={4}>
                    <Button
                      variant="outline"
                      onClick={handleClose}
                      flexGrow={1}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      flexGrow={1}
                      disabled={!isFormValid()}
                    >
                      {editInvoiceData ? "Guardar Cambios" : "Agregar"}
                    </Button>
                  </HStack>
                )}
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AddInvoiceModal;
